import { useState, useEffect } from 'react';
import { api, useToast, PanelHowTo } from './_barrel.js';


        // ===== Companies House Panel =====
        // Two-step flow: search → analyst picks officer → commit.
        // Persisted rows show as a clean table beneath.
        function CompaniesHousePanel({ token, subjectId, subject }) {
            const [chSummary, setChSummary] = useState(null);
            const [open, setOpen] = useState(false);
            const [chDraft, setChDraft] = useState("");
            const [chGenerating, setChGenerating] = useState(false);
            const [chSaving, setChSaving] = useState(false);
            const [records, setRecords] = useState([]);
            const [searchResult, setSearchResult] = useState(null);
            const [searching, setSearching] = useState(false);
            const [committing, setCommitting] = useState(false);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const r = await api(`/api/v1/subjects/${subjectId}/companies-house`, {}, token);
                        setRecords(Array.isArray(r) ? r : []);
                    } catch (e) { setError(e.message); }
                    try {
                        const sr = await api(`/api/v1/subjects/${subjectId}/companies-house/summary`, {}, token);
                        if (sr && sr.summary) { setChSummary(sr.summary); setChDraft(sr.summary); }
                    } catch (e) {}
                })();
            }, [token, subjectId, refresh]);
            const generateChDraft = async () => {
                setChGenerating(true); setError(null);
                try {
                    const r = await api(`/api/v1/subjects/${subjectId}/companies-house/summary/draft`, { method: 'POST' }, token);
                    setChDraft(r.draft || "");
                    toast("Draft generated. Review and edit before saving.");
                } catch (e) { setError(e.message); }
                finally { setChGenerating(false); }
            };
            const saveChSummary = async () => {
                setChSaving(true); setError(null);
                try {
                    const r = await api(`/api/v1/subjects/${subjectId}/companies-house/summary`, { method: 'PUT', body: JSON.stringify({ summary: chDraft }) }, token);
                    setChSummary(r.summary || null);
                    toast("Summary signed off and saved.");
                } catch (e) { setError(e.message); }
                finally { setChSaving(false); }
            };

            const runSearch = async () => {
                setSearching(true);
                setError(null);
                setSearchResult(null);
                try {
                    const r = await api(`/api/v1/subjects/${subjectId}/companies-house/search`,
                        { method: 'POST' }, token);
                    setSearchResult(r);
                    if ((r.candidates || []).length === 0 && !r.auto_picked) {
                        toast('No Companies House matches for this name');
                    }
                } catch (e) { setError(e.message); }
                finally { setSearching(false); }
            };

            const commitOfficer = async (cand) => {
                setCommitting(true);
                setError(null);
                try {
                    const body = {
                        officer_id: cand.officer_id,
                        officer_name: cand.name,
                        officer_dob_month: cand.dob_month,
                        officer_dob_year: cand.dob_year,
                        officer_appointments_count: cand.appointments_count,
                        officer_address: cand.address_snippet,
                    };
                    const res = await api(`/api/v1/subjects/${subjectId}/companies-house/commit`,
                        { method: 'POST', body: JSON.stringify(body) }, token);
                    toast(`Persisted ${res.appointments_persisted} appointment(s) for ${cand.name}`);
                    // Don't clear the whole search result — just remove the
                    // just-committed candidate from the local list so the
                    // analyst can confirm additional matches from the same
                    // search. A real subject may be a director of multiple
                    // companies under separate officer records.
                    setSearchResult(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            candidates: (prev.candidates || []).filter(
                                c => c.officer_id !== cand.officer_id
                            ),
                            auto_picked: prev.auto_picked && prev.auto_picked.officer_id === cand.officer_id
                                ? null : prev.auto_picked,
                        };
                    });
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
                finally { setCommitting(false); }
            };

            const dismissPicker = () => setSearchResult(null);

            const dismissOfficer = async (cand) => {
                try {
                    await api(`/api/v1/subjects/${subjectId}/companies-house/dismiss`,
                        { method: 'POST', body: JSON.stringify({
                            officer_id: cand.officer_id, officer_name: cand.name,
                        }) }, token);
                    // Drop this candidate from the local list immediately
                    setSearchResult(prev => prev ? {
                        ...prev,
                        candidates: prev.candidates.filter(c => c.officer_id !== cand.officer_id),
                        auto_picked: prev.auto_picked && prev.auto_picked.officer_id === cand.officer_id
                            ? null : prev.auto_picked,
                    } : prev);
                    toast(`Dismissed ${cand.name}. Won't appear in future searches.`);
                } catch (e) { setError(e.message); }
            };

            const clearAll = async () => {
                if (!confirm('Clear all Companies House records for this subject?')) return;
                try {
                    await api(`/api/v1/subjects/${subjectId}/companies-house`, { method: 'DELETE' }, token);
                    toast('Cleared');
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
            };

            // Group records by officer_id so a subject with multiple officer matches still reads cleanly
            const byOfficer = {};
            for (const r of records) {
                if (!byOfficer[r.officer_id]) byOfficer[r.officer_id] = { officer: r, rows: [] };
                byOfficer[r.officer_id].rows.push(r);
            }
            const officers = Object.values(byOfficer);

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer" onClick={() => setOpen(!open)}>
                        <div className="font-display text-xl gold">Companies House</div>
                        <div className="flex items-center gap-3">
                            {records.length > 0 && (
                                <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); clearAll(); }}>Clear</button>
                            )}
                            <button className="btn-gold" onClick={(e) => { e.stopPropagation(); runSearch(); }} disabled={searching}>
                                {searching ? 'Searching…' : (records.length ? 'Re-search' : 'Search Companies House')}
                            </button>
                            <div className="text-xs text-muted">{open ? '▾' : '▸'}</div>
                        </div>
                    </div>
                    <PanelHowTo id="companies_house" />

                    {open && (<>
                    {error && <div className="px-6 py-3 text-[var(--red)] text-sm">{error}</div>}

                    {searchResult && searchResult.auto_picked && (
                        <div className="px-6 py-4 border-b border-veil" style={{background: 'rgba(201,169,110,0.08)'}}>
                            <div className="font-mono text-xs uppercase tracking-widest gold mb-2">Suggested match — review and confirm</div>
                            <div className="flex justify-between items-start gap-4 py-2">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm">{searchResult.auto_picked.name}</div>
                                    <div className="text-xs text-muted font-mono mt-1">
                                        {searchResult.auto_picked.dob_month && searchResult.auto_picked.dob_year
                                            ? `Born ${searchResult.auto_picked.dob_month}/${searchResult.auto_picked.dob_year} · ` : ''}
                                        {searchResult.auto_picked.appointments_count != null
                                            ? `${searchResult.auto_picked.appointments_count} appointment(s) · ` : ''}
                                        {searchResult.auto_picked.address_snippet || ''}
                                    </div>
                                    <div className="text-xs text-dim font-mono mt-1">
                                        Auto-suggested: {searchResult.auto_pick_reason}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button className="btn-ghost" onClick={() => dismissOfficer(searchResult.auto_picked)}>
                                        ✕ Not them
                                    </button>
                                    <button className="btn-gold" onClick={() => commitOfficer(searchResult.auto_picked)} disabled={committing}>
                                        {committing ? '…' : '✓ Confirm match'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {searchResult && searchResult.candidates && searchResult.candidates.length > 0 && (
                        <div className="px-6 py-4 border-b border-veil">
                            <div className="flex justify-between items-center mb-3">
                                <div className="font-mono text-xs uppercase tracking-widest gold">
                                    {searchResult.auto_picked ? 'Other candidates' : 'Confirm any that are the subject'}
                                </div>
                                <button className="btn-ghost" style={{fontSize:'11px', padding:'4px 10px'}} onClick={dismissPicker}>
                                    Done
                                </button>
                            </div>
                            <div className="space-y-2">
                                {searchResult.candidates
                                    .filter(c => !searchResult.auto_picked || c.officer_id !== searchResult.auto_picked.officer_id)
                                    .map((c, idx) => (
                                    <div key={c.officer_id + '_' + idx} className="flex justify-between items-start gap-4 py-2 border-b border-veil last:border-b-0">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm">{c.name}</div>
                                            <div className="text-xs text-muted font-mono mt-1">
                                                {c.dob_month && c.dob_year ? `Born ${c.dob_month}/${c.dob_year} · ` : ''}
                                                {c.appointments_count != null ? `${c.appointments_count} appointment(s) · ` : ''}
                                                {c.address_snippet || ''}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button className="btn-ghost" onClick={() => dismissOfficer(c)}
                                                style={{fontSize: '11px', padding: '4px 10px'}}>
                                                ✕ Not them
                                            </button>
                                            <button className="btn-ghost" onClick={() => commitOfficer(c)} disabled={committing}>
                                                {committing ? '…' : '+ Add this match'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {searchResult.dismissed_filtered_count > 0 && (
                                <div className="text-xs text-dim font-mono mt-3">
                                    {searchResult.dismissed_filtered_count} previously dismissed candidate(s) hidden.
                                </div>
                            )}
                            <div className="text-xs text-muted mt-2">
                                You can confirm multiple matches if the subject appears under several officer records.
                                Click "Done" when finished.
                            </div>
                        </div>
                    )}

                    {searchResult && !searchResult.auto_picked && searchResult.candidates.length === 0 && records.length === 0 && (
                        <div className="px-6 py-4 border-b border-veil text-sm text-muted">
                            No new Companies House matches for "{subject?.full_name}". Either the subject has no UK directorships, or the name needs disambiguation.
                            {searchResult.dismissed_filtered_count > 0 && (
                                <span> ({searchResult.dismissed_filtered_count} previously dismissed candidate(s) also hidden.)</span>
                            )}
                        </div>
                    )}
                    {searchResult && !searchResult.auto_picked && searchResult.candidates.length === 0 && records.length > 0 && (
                        <div className="px-6 py-4 border-b border-veil text-sm text-muted">
                            No new candidates to add. The committed appointments below are already on file.
                            {searchResult.dismissed_filtered_count > 0 && (
                                <span> ({searchResult.dismissed_filtered_count} previously dismissed candidate(s) hidden.)</span>
                            )}
                        </div>
                    )}

                    {officers.length === 0 ? (
                        <div className="px-6 py-6 text-muted text-sm">No Companies House records stored for this subject yet.</div>
                    ) : (
                        <div>
                            {officers.map(({ officer, rows }) => (
                                <div key={officer.officer_id} className="border-b border-veil last:border-b-0">
                                    <div className="px-6 py-3 bg-[var(--surface-2)]">
                                        <div className="font-mono text-xs uppercase tracking-widest gold">
                                            {officer.officer_name} <span className="text-muted">· {rows.length} appointment{rows.length===1?'':'s'}</span>
                                        </div>
                                        <div className="text-xs text-muted font-mono mt-1">
                                            {officer.officer_dob_month && officer.officer_dob_year
                                                ? `Born ${officer.officer_dob_month}/${officer.officer_dob_year} · ` : ''}
                                            {officer.officer_address || ''}
                                        </div>
                                    </div>
                                    {rows.map(r => (
                                        <div key={r.id} className="px-6 py-3 grid grid-cols-12 gap-4 items-center text-sm">
                                            <div className="col-span-4">
                                                <div className="font-mono text-xs text-muted">{r.company_number}</div>
                                                <div>{r.company_name}</div>
                                            </div>
                                            <div className="col-span-2">
                                                <span className={`label-tag ${r.company_status === 'active' ? 'sev-low' : 'sev-moderate'}`}>
                                                    {r.company_status || '—'}
                                                </span>
                                            </div>
                                            <div className="col-span-3 text-xs text-muted">{r.officer_role_at_company}</div>
                                            <div className="col-span-3 text-xs font-mono text-dim text-right">
                                                {r.appointed_on ? `From ${r.appointed_on}` : ''}
                                                {r.resigned_on ? ` · To ${r.resigned_on}` : (r.appointed_on ? ' · current' : '')}
                                            </div>
                                            {r.company_address && (
                                                <div className="col-span-12 text-xs text-dim font-mono pl-0">
                                                    {r.company_address}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    {records.length > 0 && (
                        <div className="px-6 py-4 border-t border-veil">
                            <div className="font-mono text-xs uppercase tracking-widest gold mb-2">Analyst summary</div>
                            {chSummary && (
                                <div className="text-xs text-muted mb-2">Signed-off summary on file. Editing below replaces it on save.</div>
                            )}
                            <textarea
                                className="w-full text-sm p-3 rounded bg-[var(--surface-2)] border border-veil"
                                rows={10}
                                value={chDraft}
                                onChange={e => setChDraft(e.target.value)}
                                placeholder="Generate a draft, review and edit it, then save the signed-off version."
                            />
                            <div className="flex gap-2 mt-2">
                                <button className="btn-secondary text-xs" onClick={generateChDraft} disabled={chGenerating}>
                                    {chGenerating ? 'Generating...' : 'Generate draft'}
                                </button>
                                <button className="btn-primary text-xs" onClick={saveChSummary} disabled={chSaving || !chDraft.trim()}>
                                    {chSaving ? 'Saving...' : 'Save signed-off summary'}
                                </button>
                            </div>
                        </div>
                    )}
                    </>)}
                </div>
            );
        }

export { CompaniesHousePanel };
